// SPDX-License-Identifier: AGPL-3.0-or-later

import Channels from '@app/features/channel/state/Channels';
import Guilds from '@app/features/guild/state/Guilds';
import {withPastedTextFileNames} from '@app/features/pasted_text/PastedText';
import Users from '@app/features/user/state/Users';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';

export interface MentionNames {
	user(id: string): string | null;
	role(id: string): string | null;
	channel(id: string): string | null;
}

const FENCED_CODE = /```[\s\S]*?```/g;

/**
 * KONVO: one-line plain text for chat rows and feed previews. Mentions read as "@Lerato" and
 * "#dev" instead of raw `<@123…>` tokens, custom emoji as ":name:", and markdown markers go away
 * without eating the "#" in "PR #211" or the ">" in "a > b". A long paste reads as its file's name.
 */
export function formatMessagePreviewText(content: string, names: MentionNames): string {
	const text = withPastedTextFileNames(content)
		.replace(FENCED_CODE, ' ')
		.replace(/<@!?(\d+)>/g, (_m, id: string) => {
			const name = names.user(id);
			return name ? `@${name}` : '@…';
		})
		.replace(/<@&(\d+)>/g, (_m, id: string) => `@${names.role(id) ?? '…'}`)
		.replace(/<#(\d+)>/g, (_m, id: string) => `#${names.channel(id) ?? '…'}`)
		.replace(/<a?:(\w+):\d+>/g, ':$1:')
		.replace(/<t:(\d+)(?::[tTdDfFR])?>/g, (_m, seconds: string) => new Date(Number(seconds) * 1000).toLocaleString())
		.replace(/^\s*(?:>{1,3}|-#|#{1,3})\s+/gm, '')
		.replace(/\|\|(.+?)\|\|/g, '$1')
		.replace(/[*_~`]/g, '')
		.replace(/\s+/g, ' ')
		.trim();
	return text || firstCodeBlockLine(content);
}

/**
 * KONVO: a one-line preview never shows a fenced code block as source in a code chip. These two
 * split a message for the rows that render markdown: the text around its code blocks, and for a
 * message that is only code, the first line of that code to show as plain text.
 */
export function withoutCodeBlocks(content: string): string {
	return content.replace(FENCED_CODE, '\n').trim();
}

export function firstCodeBlockLine(content: string): string {
	const match = /```([\s\S]*?)```/.exec(content);
	if (!match) return '';
	// "```html\n…": the first line is the language, not code.
	const body = match[1].replace(/^[\w+#.-]*\n/, '');
	const line = body.split('\n').find((l) => l.trim()) ?? '';
	return line.replace(/\s+/g, ' ').trim();
}

/** The same, resolving names from the loaded stores; `guildId` picks server nicknames and roles. */
export function messagePreviewText(content: string, guildId: string | null = null): string {
	return formatMessagePreviewText(content, {
		user: (id) => {
			const user = Users.getUser(id);
			return user ? NicknameUtils.getNickname(user, guildId) : null;
		},
		role: (id) => (guildId ? (Guilds.getGuild(guildId)?.roles[id]?.name ?? null) : null),
		channel: (id) => Channels.getChannel(id)?.name ?? null,
	});
}
